import { z } from 'zod'

import { api, ApiError, authRequest } from './client'

const timezone = z.enum(['Asia/Jakarta', 'Asia/Makassar', 'Asia/Jayapura'])
export type Timezone = z.infer<typeof timezone>

const outlet = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  address: z.string(),
  phone: z.string(),
  timezone,
  receipt_footer: z.string(),
})
const business = z.object({ id: z.string(), name: z.string(), outlet })
export type Business = z.infer<typeof business>

const device = z.object({
  id: z.string(),
  letter: z.string(),
  status: z.enum(['active', 'revoked']),
  model: z.string(),
  app_version: z.string(),
  activated_at: z.string(),
  revoked_at: z.string().nullable(),
  last_synced_at: z.string().nullable(),
  pending_count: z.number().int().nullable(),
  pending_reported_at: z.string().nullable(),
})
export type Device = z.infer<typeof device>

const replacementInfo = z.object({
  active: z
    .object({
      id: z.string(),
      letter: z.string(),
      model: z.string(),
      last_synced_at: z.string().nullable(),
      pending_count: z.number().int().nullable(),
      pending_reported_at: z.string().nullable(),
      last_receipt_no: z.string().nullable(),
    })
    .nullable(),
  safe_to_replace: z.boolean(),
})
export type ReplacementInfo = z.infer<typeof replacementInfo>

// ---------- reports (P4-05/06). Money is integer rupiah; the web never sums it itself.
const money = z.number().int()
const orderType = z.enum(['dine_in', 'takeaway'])
const staffLine = z.object({
  staff_id: z.string().nullable(),
  name: z.string(),
  services: z.number().int(),
  net: money,
  commission: money,
})
export type StaffLine = z.infer<typeof staffLine>
const summary = z.object({
  from: z.string(),
  to: z.string(),
  count: z.number().int(),
  gross: money,
  discount: money,
  net: money,
  // F&B lite (ADR-0014). Defaults keep an older server's answer readable.
  tax: money.default(0),
  service: money.default(0),
  takeaway_fee: money.default(0),
  rounding: z.number().int().default(0),
  void_count: z.number().int(),
  void_amount: money,
  by_day: z.array(z.object({ day: z.string(), count: z.number().int(), net: money })),
  by_order_type: z.array(z.object({ order_type: orderType.nullable(), count: z.number().int(), net: money })).default([]),
  by_method: z.array(z.object({ method: z.string(), amount: money })),
  by_item: z.array(z.object({ name: z.string(), quantity: z.number().int(), net: money })),
  by_staff: z.array(staffLine),
})
export type Summary = z.infer<typeof summary>

const saleRow = z.object({
  id: z.string(),
  receipt_no: z.string(),
  business_day: z.string(),
  device_time: z.string(),
  utc_offset_minutes: z.number().int(),
  status: z.enum(['completed', 'voided']),
  gross_amount: money,
  discount_amount: money,
  net_amount: money,
  device_letter: z.string(),
  cashier_name: z.string(),
  methods: z.string().nullable(),
  order_type: orderType.nullable().default(null),
  table_label: z.string().nullable().default(null),
  takeaway_fee_amount: money.default(0),
  service_amount: money.default(0),
  tax_amount: money.default(0),
  rounding_amount: z.number().int().default(0),
})
export type SaleRow = z.infer<typeof saleRow>
const saleDetail = saleRow.omit({ methods: true }).extend({
  server_received_at: z.string(),
  tax_rate_bps: z.number().int().default(0),
  service_rate_bps: z.number().int().default(0),
  prices_include_tax: z.boolean().default(false),
  items: z.array(
    z.object({
      name: z.string(),
      item_type: z.string(),
      quantity: z.number().int(),
      unit_price: money,
      gross: money,
      discount: money,
      net: money,
      staff_name: z.string(),
      commission: money,
      note: z.string().nullable().default(null),
    }),
  ),
  payments: z.array(z.object({ method: z.string(), amount: money, reference: z.string().nullable() })),
  reversal: z
    .object({
      reason: z.string(),
      reversed_at: z.string(),
      business_day: z.string(),
      amount: money,
      authorized_by_name: z.string(),
    })
    .nullable(),
})
export type SaleDetail = z.infer<typeof saleDetail>

const cashSession = z.object({
  id: z.string(),
  business_day: z.string(),
  opened_at: z.string(),
  closed_at: z.string().nullable(),
  opening_cash: money,
  device_letter: z.string(),
  cash_sales: money,
  cash_in: money,
  cash_out: money,
  voids: money,
  expected_now: money,
  expected_cash: money.nullable(),
  actual_cash: money.nullable(),
  difference_amount: money.nullable(),
  notes: z.string().nullable(),
})
export type CashSession = z.infer<typeof cashSession>

const syncStatus = z.object({
  id: z.string(),
  letter: z.string(),
  status: z.enum(['active', 'revoked']),
  model: z.string(),
  app_version: z.string(),
  last_synced_at: z.string().nullable(),
  last_seen_at: z.string().nullable(),
  rejected: z.number().int(),
  waiting: z.number().int(),
})
export type SyncStatus = z.infer<typeof syncStatus>

// ---------- master data (P4-08)
const catalogItem = z.object({
  id: z.string(),
  name: z.string(),
  item_type: z.enum(['service', 'product']),
  category: z.string(),
  price: money,
  cost: money.nullable(),
  active: z.boolean(),
  version: z.number().int(),
})
export type CatalogItem = z.infer<typeof catalogItem>
const staff = z.object({ id: z.string(), name: z.string(), active: z.boolean(), version: z.number().int() })
export type Staff = z.infer<typeof staff>
const rule = z.object({
  id: z.string(),
  catalog_item_id: z.string(),
  staff_id: z.string().nullable(),
  rule_type: z.enum(['fixed', 'percent']),
  rate: money,
  active: z.boolean(),
  version: z.number().int(),
})
export type Rule = z.infer<typeof rule>
const saved = z.object({ id: z.string(), version: z.number().int(), snapshot_version: z.number().int().nullable() })
const conflict = z.object({
  id: z.string(),
  entity_type: z.string(),
  entity_id: z.string(),
  entity_name: z.string().nullable(),
  field: z.string(),
  device_value: z.unknown(),
  server_value: z.unknown(),
  created_at: z.string(),
  seen_by_owner_at: z.string().nullable(),
})
export type Conflict = z.infer<typeof conflict>
const auditRow = z.object({
  id: z.number().int(),
  actor_type: z.string(),
  actor_id: z.string().nullable(),
  action: z.string(),
  entity_type: z.string().nullable(),
  entity_id: z.string().nullable(),
  detail: z.record(z.string(), z.unknown()),
  created_at: z.string(),
})
export type AuditRow = z.infer<typeof auditRow>

const activationCode = z.object({ code: z.string(), kind: z.enum(['normal', 'replace']), expires_at: z.string() })
export type ActivationCode = z.infer<typeof activationCode>

const me = z.object({ user_id: z.string(), name: z.string(), phone: z.string() })

// ---------- cashier settings (ADR-0013). Names are the server's.
export type BusinessType = 'services' | 'food'
const settings = z.object({
  business_type: z.enum(['services', 'food']),
  staff_per_line_enabled: z.boolean(),
  tax_enabled: z.boolean(),
  tax_rate_bps: z.number().int(),
  service_enabled: z.boolean(),
  service_rate_bps: z.number().int(),
  prices_include_tax: z.boolean(),
  rounding_unit: z.union([z.literal(0), z.literal(100)]),
  order_type_enabled: z.boolean(),
  takeaway_fee_amount: z.number().int(),
  table_number_enabled: z.boolean(),
  item_notes_enabled: z.boolean(),
  note_presets: z.array(z.string()),
  receipt_show_notes: z.boolean(),
  kitchen_ticket_enabled: z.boolean(),
  kitchen_ticket_copies: z.number().int(),
  open_bill_enabled: z.boolean(),
})
export type CashierSettings = z.infer<typeof settings>
export type CashierSettingsChange = Partial<Omit<CashierSettings, 'business_type'>>

// ---------- receipt logo (ADR-0013): a 1-bit bitmap, see logo.ts
const savedLogo = z.object({ width: z.number().int(), height: z.number().int(), data: z.string() })
export type SavedLogo = z.infer<typeof savedLogo>

// ---------- subscription (P6-10)
const order = z.object({
  order_id: z.string(),
  plan_code: z.string(),
  months: z.number().int(),
  gross_amount: z.number().int(),
  status: z.enum(['pending', 'paid', 'failed', 'expired', 'refunded']),
  redirect_url: z.string().nullable(),
  created_at: z.string(),
  paid_at: z.string().nullable(),
})
export type Order = z.infer<typeof order>
const subscription = z.object({
  state: z.enum(['trial', 'active', 'grace', 'expired']),
  plan: z.string(),
  entitled_until: z.string(),
  grace_until: z.string(),
  days_left: z.number().int(),
  trial_ends_at: z.string(),
  paid_until: z.string().nullable(),
  checkout_available: z.boolean(),
  plans: z.array(
    z.object({ code: z.string(), name: z.string(), months: z.number().int(), days: z.number().int(), price: z.number().int() }),
  ),
  orders: z.array(order),
})
export type Subscription = z.infer<typeof subscription>

const json = (method: string, body: unknown): RequestInit => ({ method, body: JSON.stringify(body) })

export const endpoints = {
  login: (phone: string, password: string) => authRequest('/v1/auth/login', { phone, password }),
  signup: (data: {
    invite_code: string
    phone: string
    password: string
    name: string
    business_name: string
    outlet_name: string
    outlet_code: string
    timezone: Timezone
    business_type: BusinessType
  }) => authRequest('/v1/auth/signup', data),
  changePassword: (current_password: string, new_password: string) =>
    api('/v1/auth/password', z.object({ access_token: z.string() }), json('POST', { current_password, new_password })),
  logout: () => api('/v1/auth/logout', z.undefined(), { method: 'POST' }),
  me: () => api('/v1/auth/me', me),
  business: () => api('/v1/business', business),
  updateOutlet: (changes: Partial<Pick<Business['outlet'], 'address' | 'phone' | 'receipt_footer' | 'timezone'>>) =>
    api('/v1/business/outlet', business, json('PATCH', changes)),
  devices: () => api('/v1/devices', z.array(device)),
  createActivationCode: (kind: 'normal' | 'replace', acknowledge_unsent = false) =>
    api('/v1/devices/activation-codes', activationCode, json('POST', { kind, acknowledge_unsent })),
  replacementInfo: () => api('/v1/devices/replacement-info', replacementInfo),

  summary: (from: string, to: string) => api(`/v1/reports/summary?${range(from, to)}`, summary),
  sales: (from: string, to: string, q = '', offset = 0) =>
    api(`/v1/reports/sales?${range(from, to)}&q=${encodeURIComponent(q)}&limit=50&offset=${offset}`, z.array(saleRow)),
  sale: (id: string) => api(`/v1/reports/sales/${encodeURIComponent(id)}`, saleDetail),
  cashSessions: (from: string, to: string) => api(`/v1/reports/cash-sessions?${range(from, to)}`, z.array(cashSession)),
  syncStatus: () => api('/v1/reports/sync-status', z.array(syncStatus)),

  catalog: () => api('/v1/catalog/items', z.array(catalogItem)),
  saveItem: (id: string, body: Omit<CatalogItem, 'id' | 'version'> & { expected_version: number }) =>
    api(`/v1/catalog/items/${id}`, saved, json('PUT', body)),
  staff: () => api('/v1/staff', z.array(staff)),
  saveStaff: (id: string, body: { name: string; active: boolean; expected_version: number }) =>
    api(`/v1/staff/${id}`, saved, json('PUT', body)),
  rules: () => api('/v1/commission-rules', z.array(rule)),
  saveRule: (id: string, body: Omit<Rule, 'id' | 'version'> & { expected_version: number }) =>
    api(`/v1/commission-rules/${id}`, saved, json('PUT', body)),
  conflicts: () => api('/v1/master-conflicts', z.array(conflict)),
  conflictsSeen: () => api('/v1/master-conflicts/seen', z.object({ marked: z.number().int() }), { method: 'POST' }),
  audit: () => api('/v1/audit?limit=500', z.array(auditRow)),

  /** The outlet's receipt logo, or null when it has none. */
  logo: async (): Promise<SavedLogo | null> => {
    try {
      return await api('/v1/business/logo', savedLogo)
    } catch (e) {
      if (e instanceof ApiError && e.code === 'no_logo') return null
      throw e
    }
  },
  saveLogo: (body: SavedLogo) => api('/v1/business/logo', savedLogo, json('PUT', body)),
  deleteLogo: () => api('/v1/business/logo', z.undefined(), { method: 'DELETE' }),

  cashierSettings: () => api('/v1/business/settings', settings),
  saveCashierSettings: (changes: CashierSettingsChange) => api('/v1/business/settings', settings, json('PATCH', changes)),
  applyProfile: (business_type: BusinessType) =>
    api('/v1/business/settings/apply-profile', settings, json('POST', { business_type })),

  subscription: () => api('/v1/billing/subscription', subscription),
  createOrder: (plan_code: string) => api('/v1/billing/orders', order, json('POST', { plan_code })),
  order: (id: string) => api(`/v1/billing/orders/${encodeURIComponent(id)}`, order),
  redeemVoucher: (code: string) =>
    api('/v1/billing/vouchers/redeem', z.object({ days: z.number().int(), paid_until: z.string().nullable() }), json('POST', { code })),
}

function range(from: string, to: string): string {
  return `from=${from}&to=${to}`
}
